import { useEffect, useState } from 'react';
import { BrandLoader } from './BrandLoader';

/**
 * Splash screen — minimalistyczny welcome / logout overlay.
 * - WelcomeSplash: pierwsze logowanie w sesji (3 s loading)
 * - LogoutSplash: po klikni "Wyloguj" zanim leci redirect (2.5 s)
 *
 * Design: full-screen overlay, motyw-aware tło (light/dark), kompaktowe
 * logo (md = 80 px wys.), subtelny tagline, cienki pasek z gradientem.
 *
 * Eliminacja "skoku rozmiaru" vs poprzedniej wersji (h-80 = 320 px):
 * - LogoMark md (80–96 px) zamiast h-80 (320 px) — proporcjonalne, nie krzykliwe
 * - Image preloadowany w index.html → zero opoznienia ladowania
 * - Spójność z RouteLoader (oba uzywaja LogoMark)
 *
 * Animacje: fade-in 200 ms (logo + tagline z opoznieniem 150 ms dla efektu kaskady),
 * pasek wypełnia sie liniowo, fade-out 300 ms.
 */
export function SplashScreen({
  durationMs = 3000,
  onComplete,
  tagline = 'Platforma CoreLTB Builders',
  status,
}: {
  /** Czas trwania w ms (default 3000) */
  durationMs?: number;
  /** Callback po zakonczeniu (po fade-out) */
  onComplete: () => void;
  /** Subtelny tagline pod logo. Empty string → bez tagline. */
  tagline?: string;
  /** Tekst pod paskiem, np. „Wylogowywanie…”. */
  status?: string;
}) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setExiting(true), durationMs);
    const completeTimer = setTimeout(() => onComplete(), durationMs + 300);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(completeTimer);
    };
  }, [durationMs, onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[100] transition-opacity duration-300 ${exiting ? 'opacity-0' : 'opacity-100'}`}
      aria-hidden={exiting}
    >
      <BrandLoader durationMs={durationMs} tagline={tagline} status={status} />
    </div>
  );
}

/**
 * Sprawdza czy WelcomeSplash powinien byc pokazany w tej sesji.
 * Pokazuje sie raz po pierwszym otwarciu app.coreltb.pl po loginie.
 * Po wyswietleniu ustawia flag w sessionStorage — kolejne nawigacje w tej
 * samej sesji nie pokazuja go.
 */
export function useShouldShowWelcomeSplash(): {
  shouldShow: boolean;
  markShown: () => void;
} {
  const [shouldShow, setShouldShow] = useState(() => {
    try {
      return window.sessionStorage.getItem('coreltb.welcomeShown') !== '1';
    } catch {
      return false;
    }
  });

  const markShown = () => {
    try {
      window.sessionStorage.setItem('coreltb.welcomeShown', '1');
    } catch {
      // sessionStorage disabled — splash sie pokaze przy kazdym F5
    }
    setShouldShow(false);
  };

  return { shouldShow, markShown };
}
